import {
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { catchError, Observable, throwError } from 'rxjs';
import { BusinessAuthService } from '../services/business-auth.service';
import { environment } from '../../environments/environment';

/** Formato t?pico de error del backend Nest/API. */
interface BackendErrorBody {
  timestamp?: string;
  path?: string;
  statusCode?: number;
  message?: string;
  error?: string;
}

function isBusinessApiRequest(url: string): boolean {
  const base = environment.apiUrl.replace(/\/$/, '');
  return url.startsWith(base) && url.includes('/business/');
}

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const alertController = inject(AlertController);
  const router = inject(Router);
  const businessAuth = inject(BusinessAuthService);
  const ngZone = inject(NgZone);

  const excludedUrls: (string | RegExp)[] = [
    'auth/refresh-token',
    'auth/me',
    'driver/me',
    /\/trip\/.*\/location/,
    /\/trip\/trip\/[^/]+\/route/,
    'system-configuration/current',
    'trip-operation/accept',
    '/trip/driver/location',
    /\/trip-operation\/[^/]+\/in-progress/,
    /\/packages\/[^/]+\/pickup/,
    /\/packages\/[^/]+\/deliver/,
    'business/account/password',
    'business-login',
    'auth/business/validate-reset-token',
    /** Ruta p?blica de seguimiento (sin sesi?n): no mostrar alert global en 4xx/5xx. */
    /\/trip\/public\/nano\/[^/]+\/route/,
    /\/public\/trip\/business-trip-link\//,
    /\/surveys\/by-trip\//,
    'fare-rates/calculate-all-services-google'
  ];

  const isExcludedUrl = (url: string): boolean =>
    excludedUrls.some((excludedUrl) => {
      if (typeof excludedUrl === 'string') {
        return url.includes(excludedUrl);
      }
      return excludedUrl.test(url);
    });

  const showAlert = (title: string, message: string): void => {
    void ngZone.run(async () => {
      const alert = await alertController.create({
        header: title,
        message,
        buttons: [
          {
            text: 'Aceptar',
            role: 'confirm',
            handler: () => {
              if (message === 'No existe un viaje registrado con este id') {
                router.navigate(['/'], { replaceUrl: true });
              }
            },
          },
        ],
      });
      await alert.present();
    });
  };

  const handleBusinessAuthError = (): void => {
    businessAuth.logout();
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorTitle = 'Aviso';
      let errorMessage = 'Ocurri? un error inesperado';
      let shouldRedirectToLogin = false;

      const body = error.error as BackendErrorBody | undefined;

      console.log('Error interceptor:', {
        status: error.status,
        url: req.url,
        error: error.error,
        message: body?.message,
        errorType: body?.error,
        timestamp: body?.timestamp,
        path: body?.path,
      });

      switch (error.status) {
        case 400:
          if (error.error) {
            errorTitle = 'Aviso';
            const msg = body?.message;
            if (Array.isArray(msg)) {
              errorMessage = String(msg[0]);
            } else {
              errorMessage =
                (typeof msg === 'string' ? msg : undefined) ||
                body?.error ||
                'Solicitud incorrecta';
            }
          } else {
            errorMessage = 'Solicitud incorrecta. Verifica los datos enviados.';
          }
          break;

        case 401:
          shouldRedirectToLogin = true;
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? '';
          } else {
            errorMessage =
              'Tu sesi?n ha expirado. Por favor, inicia sesi?n nuevamente.';
          }
          break;

        case 403:
          if (body?.message === 'Cuenta eliminada') {
            errorMessage = body.error ?? errorMessage;
          } else {
            errorMessage = 'No tienes permisos para realizar esta acci?n.';
          }

          if (req.url.includes('/trip/by-id/')) {
            return throwError(() => error);
          }
          if (
            req.url.includes('auth/me') ||
            req.url.includes('driver/me') ||
            req.url.includes('driver-earnings/stats/daily') ||
            req.url.includes('driver-earnings/trip-bonus-progress') ||
            req.url.includes('trip/driver/pending') ||
            req.url.includes('packages/driver/pending') ||
            req.url.includes('trip/driver/scheduled')
          ) {
            if (isBusinessApiRequest(req.url)) {
              businessAuth.logout();
            }
            return throwError(() => error);
          }
          break;

        case 404:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? 'El recurso solicitado no fue encontrado.';
          } else {
            errorMessage = 'El recurso solicitado no fue encontrado.';
          }

          if (body?.message === 'Usuario no encontrado') {
            if (isBusinessApiRequest(req.url)) {
              businessAuth.logout();
            }
          }
          break;

        case 409:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? 'Conflicto con el recurso solicitado.';
          } else {
            errorMessage = 'Conflicto con el recurso solicitado.';
          }
          break;

        case 422:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? 'Datos de validaci?n incorrectos.';
          } else {
            errorMessage = 'Datos de validaci?n incorrectos.';
          }
          break;

        case 429:
          errorMessage =
            'Demasiadas solicitudes. Intenta nuevamente en unos minutos.';
          break;

        case 500:
          errorMessage =
            'Error interno del servidor. Intenta nuevamente m?s tarde.';
          break;

        case 502:
        case 503:
        case 504:
          errorMessage =
            'Servicio temporalmente no disponible. Intenta nuevamente m?s tarde.';
          break;

        default:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? errorMessage;
          } else if (error.message) {
            errorTitle = error.message;
          } else {
            errorMessage =
              'Error de conexi?n. Verifica tu conexi?n a internet.';
          }
          break;
      }

      console.log('errorTitle', errorTitle);
      console.log('errorMessage', errorMessage);

      if (error.status === 0 || error.message?.includes('ERR_CONNECTION_RESET')) {
        return throwError(() => error);
      }

      if (isExcludedUrl(req.url)) {
        return throwError(() => error);
      }

      showAlert(errorTitle, errorMessage);

      if (shouldRedirectToLogin && isBusinessApiRequest(req.url)) {
        handleBusinessAuthError();
      }

      return throwError(() => error);
    })
  );
};
